/**
 * A panel that opens **over** the arena without taking the arena away.
 *
 * ## Why this exists, and what it reverses
 *
 * The roster behind the population badge and the sayings picker were both the app's own
 * `Dialog`, chosen — and the reasoning is worth re-stating rather than deleting — for Escape,
 * the backdrop, the focus trap and `role="dialog"`, which is the marker `keyboardActs` in
 * `../scenes/steering.ts` reads to stop a SPACE meant for a control inside the panel from also
 * splitting the player's cluster.
 *
 * Every one of those is right for a **modal**, and a modal is the wrong thing for a panel a
 * player opens *mid-chase*. The player report: "if they open it during gameplay, they can still
 * try to outrun circles if one gets close." A `Dialog` blocks both halves of that — the backdrop
 * eats every pointer move before it reaches the canvas, so pointer steering stops dead, and
 * `role="dialog"` disarms the action keys. So this is a panel that is deliberately **not** a
 * dialog:
 *
 * - **Nothing is blocked.** The panel itself is `pointer-events-auto` and there is no backdrop at
 *   all, so every pointer sample that is not on the card reaches the canvas and steers — the
 *   arrangement `ActionButtons` is already under inside the HUD's `pointer-events-none` overlay.
 * - **It carries no `role="dialog"`**, so `keyboardActs` answers true and the arrows, A/S/D,
 *   SPACE, W and Q all keep working while it is up. The predicate itself is untouched: it is
 *   exactly right for `HudSettings` and for the duel overlay, which are the two overlays that
 *   *should* hold the keyboard. What changed is that these panels stopped claiming to be modals.
 *   The cost is one honest edge: SPACE while the focus is on a control *inside* the panel both
 *   activates that control and splits the cluster. That is the trade the report asked for, and it
 *   is the reason the arrows and A/S/D — the keys somebody actually escapes with — are the ones
 *   that matter here.
 * - **Escape still closes it and the ✕ is still there**, because losing the modality must not
 *   cost the two ways out a keyboard has. There is no focus trap and there must not be one: a
 *   trap is the thing that would keep a player inside the panel while a bigger circle arrives.
 * - **It is bounded and it scrolls inside itself.** A full room is 200 names; the card is capped
 *   in height and in width so the arena is still readable around it on a phone, and the list
 *   scrolls rather than the panel growing.
 *
 * ## Which overlay is which
 *
 * Not every overlay in the arena wants this, and the four are decided one at a time:
 *
 * - **The room roster** (`PopulationBadge`) — an `ArenaPanel`. It is a *reference* panel about
 *   the room, opened mid-run to answer "who are these circles"; nothing about reading it is a
 *   reason to stop playing.
 * - **The sayings picker** (`components/sayings/sayings-picker`) — an `ArenaPanel`. Saying "gg"
 *   is something a player does while being chased.
 * - **Overlay settings** (`HudSettings`) — **stays a `Dialog`, deliberately.** It is a deliberate
 *   pause: eleven switches, read one at a time, by somebody who is not steering. It is also the
 *   one overlay whose controls a stray SPACE would flip, so `keyboardActs` holding the keyboard
 *   there is a feature rather than the cost it is here.
 * - **The death panel** (`DeathScreen`) — already exactly this and left alone: a translucent card
 *   inside a `pointer-events-none` layer, over an arena that is still running behind it.
 * - **The duel overlay** (`DuelOverlay`) — **stays `role="dialog"`, deliberately.** A duellist is
 *   frozen: it cannot move, split, eject or fire, so holding the keyboard costs that player
 *   nothing at all and stops a SPACE meant for a duel answer from queueing a split that would go
 *   off the moment the duel released them.
 *
 * Positioning is entirely the caller's (`className`), because the two panels want different
 * things: the roster docks to the left under the badge that opened it, and the sayings picker
 * sits above the controls it belongs to. `portal` is for the second of those — the portrait
 * footer carries `backdrop-blur`, which makes it the containing block for any `fixed` descendant,
 * so a panel that has to escape the strip it is drawn in goes through `document.body` instead.
 */

import { type CSSProperties, type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { HUD_PANEL_CLASS } from './panel';
import { useTapActivate } from './tap-activate';

export interface ArenaPanelProps {
  open: boolean;
  onClose: () => void;
  /** The heading, and the accessible name of the region. */
  title: string;
  /** One line under the heading. Optional — the roster has one, the picker's is conditional. */
  description?: string;
  /**
   * Where the panel goes **and how big it is**. Both live at the call site: the two panels
   * want different widths, stacking contexts and caps, and a default here plus an override
   * there would be two `w-*` (or two `z-*`) classes on one element deciding it by stylesheet
   * order rather than by intent.
   */
  className?: string;
  /** Rendered through `document.body`. See the header for the one reason to want that. */
  portal?: boolean;
  /** So the control that opens it can point `aria-controls` at it. */
  id?: string;
  testId?: string;
  /**
   * Positioning a stylesheet cannot express, because it is measured at runtime.
   *
   * `className` stays the way a *fixed* position is stated; this is for a panel anchored to
   * something that moves — the sayings picker opens above its own button, wherever that button
   * was drawn (`hud/anchor.ts`). It is merged after the classes, so a style always wins, which
   * is the only order that lets a caller keep a class-based fallback for the frame before it has
   * a measurement.
   */
  style?: CSSProperties;
  children: ReactNode;
}

export function ArenaPanel({
  open,
  onClose,
  title,
  description,
  className = '',
  portal = false,
  id,
  testId,
  style,
  children,
}: ArenaPanelProps) {
  const close = useTapActivate(onClose);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const panel = (
    /*
     * A landmark, not a dialog. A named `<section>` is the `region` role, which gives the panel a
     * name a screen reader can jump to *and skip out of* — the accessible shape of "this is a
     * panel you can ignore", the opposite of the "you are in here until you close it" a dialog
     * announces.
     */
    <section
      id={id}
      data-testid={testId}
      aria-label={title}
      className={`pointer-events-auto flex flex-col overflow-hidden ${HUD_PANEL_CLASS} ${className}`}
      style={style}
    >
      <div className="flex items-start gap-2 px-2.5 pb-1 pt-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
            {title}
          </h2>
          {description !== undefined && (
            <p className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{description}</p>
          )}
        </div>
        <button
          type="button"
          // On `pointerup`, not on `click`: this panel is drawn over a live arena, where a thumb
          // is routinely already down on the joystick and the platform then reports no tap at
          // all. See `./tap-activate.ts`.
          {...close}
          aria-label="Close"
          className="-mr-0.5 -mt-0.5 shrink-0 cursor-pointer rounded-md p-1 text-muted-foreground transition-opacity hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <svg
            className="size-3.5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6 6 18" />
            <path d="m6 6 12 12" />
          </svg>
        </button>
      </div>
      {/* `min-h-0` is what lets the cap above actually bite: without it a flex child refuses to
          shrink below its content and the panel grows past the arena instead of scrolling. */}
      <div className="min-h-0 flex-1 overflow-y-auto px-2.5 pb-2">{children}</div>
    </section>
  );

  return portal ? createPortal(panel, document.body) : panel;
}
